
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Header />
      
      <div className="container mx-auto px-6 py-24">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-8">Terms of Service</h1>
          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-8 space-y-6">
              
              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-300 leading-relaxed">
                  By accessing and using The Bulk Image Generator platform, you accept and agree to be bound 
                  by the terms and provisions of this agreement. If you do not agree to abide by these terms, 
                  please do not use our AI-powered bulk image generation service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">2. Service Description</h2>
                <p className="text-gray-300 leading-relaxed">
                  The Bulk Image Generator provides AI-powered bulk image generation services that allow users to create 
                  multiple high-quality images simultaneously based on text prompts. Our platform offers various AI models, 
                  batch processing capabilities, and advanced customization options for efficient image creation workflows.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">3. User Accounts and Registration</h2>
                <p className="text-gray-300 leading-relaxed">
                  To use our bulk image generation services, you must create an account and provide accurate, complete information. 
                  You are responsible for maintaining the confidentiality of your account credentials, API keys, and for all activities 
                  that occur under your account. You must notify us immediately of any unauthorized use of your account.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">4. Acceptable Use Policy</h2>
                <p className="text-gray-300 leading-relaxed">
                  When using our AI image generation platform, you agree not to:
                </p>
                <ul className="text-gray-300 list-disc pl-6 space-y-2">
                  <li>Generate illegal, harmful, threatening, abusive, harassing, or defamatory content</li>
                  <li>Create images that infringe on intellectual property rights or copyrighted material</li>
                  <li>Generate content depicting real individuals without proper consent or authorization</li>
                  <li>Create adult content, violence, or other inappropriate material as defined by our content policy</li>
                  <li>Attempt to reverse engineer, hack, or circumvent our AI models or security measures</li>
                  <li>Use the service to spam, distribute malware, or engage in fraudulent activities</li>
                  <li>Exceed usage limits or attempt to bypass rate limiting mechanisms</li>
                  <li>Resell or redistribute our AI generation services without proper licensing</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">5. Content Ownership and Intellectual Property</h2>
                <p className="text-gray-300 leading-relaxed">
                  You retain ownership rights to the images you generate through our platform, subject to these terms. 
                  However, you grant us a license to use your prompts and generated content for service improvement, 
                  AI model training, and platform development in an anonymized manner. We respect your intellectual 
                  property and expect the same consideration for our proprietary AI technology and platform.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">6. Subscription Plans and Billing</h2>
                <p className="text-gray-300 leading-relaxed">
                  Our bulk image generation services are offered through various subscription plans and usage-based pricing. 
                  By providing payment information, you authorize us to charge the applicable fees for your selected plan 
                  and usage. All payments are processed securely and are generally non-refundable unless otherwise stated 
                  in our refund policy or required by applicable law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">7. Service Availability and Performance</h2>
                <p className="text-gray-300 leading-relaxed">
                  We strive to maintain high service availability and fast processing times for bulk image generation, 
                  but do not guarantee uninterrupted access or specific generation speeds. We may temporarily suspend 
                  service for maintenance, AI model updates, infrastructure improvements, or other operational reasons. 
                  Queue processing times may vary based on demand and system load.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibent text-white mb-4">8. AI Model Usage and Limitations</h2>
                <p className="text-gray-300 leading-relaxed">
                  Our AI models are designed to generate high-quality images but may occasionally produce unexpected results. 
                  We do not guarantee the accuracy, appropriateness, or quality of every generated image. Users are responsible 
                  for reviewing generated content before use and ensuring compliance with applicable laws and regulations.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">9. Privacy and Data Protection</h2>
                <p className="text-gray-300 leading-relaxed">
                  Your privacy and data security are important to us. Please review our Privacy Policy to understand 
                  how we collect, use, store, and protect your information, prompts, and generated images. We implement 
                  industry-standard security measures to protect your data and creative work.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">10. Limitation of Liability</h2>
                <p className="text-gray-300 leading-relaxed">
                  We provide The Bulk Image Generator service "as is" without warranties of any kind, express or implied. 
                  We shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising 
                  from your use of our AI image generation platform, including but not limited to business losses, 
                  data loss, or service interruptions.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">11. Account Termination and Suspension</h2>
                <p className="text-gray-300 leading-relaxed">
                  We may terminate or suspend your account and access to our bulk image generation service at any time, 
                  with or without notice, for any reason, including violation of these terms, abuse of our platform, 
                  or non-payment of fees. Upon termination, you may download your generated images within 30 days, 
                  after which your data may be permanently deleted.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">12. API Usage and Integration</h2>
                <p className="text-gray-300 leading-relaxed">
                  If you use our API for integration with your applications, you agree to comply with our API terms, 
                  rate limits, and usage guidelines. You are responsible for securing your API keys and monitoring 
                  your usage to avoid exceeding allocated limits. Unauthorized API usage may result in service suspension.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">13. Changes to Terms</h2>
                <p className="text-gray-300 leading-relaxed">
                  We reserve the right to modify these terms at any time to reflect changes in our service, 
                  legal requirements, or business practices. We will notify users of material changes via email 
                  or through our platform. Continued use of The Bulk Image Generator after changes constitutes 
                  acceptance of the modified terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">14. Governing Law and Disputes</h2>
                <p className="text-gray-300 leading-relaxed">
                  These terms shall be governed by and construed in accordance with applicable laws. Any disputes 
                  arising from your use of our service shall be resolved through binding arbitration or in courts 
                  of competent jurisdiction, as specified in our dispute resolution policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">15. Contact Information</h2>
                <p className="text-gray-300 leading-relaxed">
                  For questions about these terms, our service, or legal matters, please contact us:
                </p>
                <div className="text-gray-300 mt-4 space-y-2">
                  <p><strong>Email:</strong> support@aigeneratedbulkimages.com</p>
                  <p><strong>Platform:</strong> The Bulk Image Generator</p>
                  <p><strong>Service:</strong> AI-Powered Bulk Image Generation</p>
                </div>
                <p className="text-gray-300 leading-relaxed mt-4">
                  We are committed to providing excellent service and will respond to your inquiries promptly. 
                  For urgent legal matters, please include "Legal - Terms of Service" in your email subject line.
                </p>
              </section>

              <div className="border-t border-white/10 pt-6 mt-8">
                <p className="text-gray-400 text-sm">
                  Last Updated: December 2024 • The Bulk Image Generator Terms of Service
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TermsOfService;
